/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */
/* eslint-disable max-lines */
import {
  ConnectionState,
  RemoteTrackPublication,
  RemoteVideoTrack,
  Room,
  RoomEvent,
  TrackEvent,
  TrackPublication,
} from 'livekit-client';
import { ICameraTarget, isVideoVisible, videoProgress } from './cameraHealth';

const POLL_MS = 2000;
const STALL_MS = 5000;
const CONNECTION_GRACE_MS = 15000;
const RETRY_DELAY_MS = 15000;
const MAX_ATTEMPTS = 3;
const HEALTHY_RESET_MS = 30000;

interface IRecoveryState {
  adaptive: boolean;
  attempts: number;
  element?: HTMLVideoElement;
  exhausted: boolean;
  healthySince?: number;
  lastProgress: number;
  nextAttempt: number;
  onSubscription: (update: { subscribe: boolean }) => void;
  participant: ICameraTarget['participant'];
  progress?: number;
  resumeAt?: number;
  targets: Set<ICameraTarget['getElement']>;
  track?: RemoteTrackPublication['track'];
  updatingSubscription: boolean;
  waitingForRecovery: boolean;
}

// One controller per output room preserves retry limits across layout changes.
export class CameraTrackRecovery {
  private states = new Map<RemoteTrackPublication, IRecoveryState>();

  private timer?: ReturnType<typeof setInterval>;

  private connectionGraceUntil = 0;

  // eslint-disable-next-line no-useless-constructor
  constructor(private room: Room) {}

  start() {
    if (this.timer) return;
    this.room.on(
      RoomEvent.ConnectionStateChanged,
      this.onConnectionStateChanged,
    );
    this.onConnectionStateChanged(this.room.state);
    this.timer = setInterval(this.check, POLL_MS);
  }

  private onConnectionStateChanged = (connection: ConnectionState) => {
    this.connectionGraceUntil =
      connection === ConnectionState.Connected
        ? Date.now() + CONNECTION_GRACE_MS
        : Infinity;
    this.states.forEach(state => this.resetObservation(state));
  };

  isResetting(publication: RemoteTrackPublication) {
    return this.states.get(publication)?.resumeAt !== undefined;
  }

  stop() {
    clearInterval(this.timer);
    this.timer = undefined;
    this.room.off(
      RoomEvent.ConnectionStateChanged,
      this.onConnectionStateChanged,
    );
    this.states.forEach((state, publication) => {
      publication.off(TrackEvent.UpdateSubscription, state.onSubscription);
    });
    this.states.clear();
  }

  register({ getElement, participant, publication }: ICameraTarget) {
    let state = this.states.get(publication);
    if (!state) {
      state = {
        adaptive: false,
        attempts: 0,
        exhausted: false,
        lastProgress: Date.now(),
        nextAttempt: 0,
        onSubscription: update => {
          const current = this.states.get(publication);
          // An explicit unsubscribe takes precedence over our pending retry.
          if (current && !current.updatingSubscription && !update.subscribe) {
            current.resumeAt = undefined;
          }
        },
        participant,
        targets: new Set(),
        updatingSubscription: false,
        waitingForRecovery: false,
      };
      this.states.set(publication, state);
      publication.on(TrackEvent.UpdateSubscription, state.onSubscription);
    }
    state.targets.add(getElement);
    return () => {
      state.targets.delete(getElement);
      if (state.targets.size === 0) this.resetObservation(state);
    };
  }

  private resetObservation(state: IRecoveryState) {
    state.lastProgress = Date.now();
    state.progress = undefined;
    state.healthySince = undefined;
    state.element = undefined;
  }

  private log(
    event: string,
    publication: RemoteTrackPublication,
    state: IRecoveryState,
    detail: Record<string, unknown> = {},
  ) {
    console.warn('[output-camera-recovery]', {
      event,
      room: this.room.name,
      participantSid: state.participant.sid,
      trackSid: publication.trackSid,
      attempt: state.attempts,
      subscriptionStatus: publication.subscriptionStatus,
      streamState: publication.track?.streamState,
      readyState: state.element?.readyState,
      progress: state.progress,
      ...detail,
    });
  }

  private setSubscribed(
    publication: RemoteTrackPublication,
    state: IRecoveryState,
    subscribed: boolean,
  ) {
    state.updatingSubscription = true;
    try {
      publication.setSubscribed(subscribed);
    } finally {
      state.updatingSubscription = false;
    }
  }

  private recover(publication: RemoteTrackPublication, state: IRecoveryState) {
    const missing = !publication.isSubscribed || !publication.track;
    if (state.attempts >= MAX_ATTEMPTS) {
      if (!state.exhausted) this.log('exhausted', publication, state);
      state.exhausted = true;
      return;
    }
    state.attempts += 1;
    state.waitingForRecovery = true;
    state.nextAttempt = Date.now() + RETRY_DELAY_MS * 2 ** (state.attempts - 1);
    this.log('retry', publication, state, {
      reason: missing ? 'missing-subscription' : 'no-video-progress',
    });
    if (missing) {
      this.setSubscribed(publication, state, true);
    } else if (state.attempts === 1 && state.element) {
      // Repair playback/attachment first, without interrupting the subscription.
      publication.track!.detach(state.element);
      publication.track!.attach(state.element);
      state.element.play().catch(error => {
        this.log('playback-error', publication, state, {
          error: String(error),
        });
      });
    } else {
      // Allow the unsubscribe to reach the server before requesting a fresh track.
      state.resumeAt = Date.now() + POLL_MS;
      this.setSubscribed(publication, state, false);
    }
    this.resetObservation(state);
  }

  private checkPublication(
    publication: RemoteTrackPublication,
    state: IRecoveryState,
  ) {
    const { participant } = state;
    if (
      this.room.remoteParticipants.get(participant.identity) !== participant ||
      participant.getTrackPublication(publication.source) !== publication
    ) {
      publication.off(TrackEvent.UpdateSubscription, state.onSubscription);
      this.states.delete(publication);
      return;
    }
    const element = Array.from(state.targets)
      .map(get => get())
      .find(isVideoVisible);
    // Adaptive streaming owns the enabled flag; manual setEnabled is rejected
    // by the SDK for these tracks. Retain this knowledge during our reset.
    if (publication.track) {
      state.adaptive =
        publication.track instanceof RemoteVideoTrack &&
        publication.track.isAdaptiveStream;
    }
    if (
      this.room.state !== ConnectionState.Connected ||
      !element ||
      publication.isMuted ||
      (!publication.isEnabled && !state.adaptive) ||
      publication.permissionStatus !==
        TrackPublication.PermissionStatus.Allowed ||
      (!publication.isDesired && state.resumeAt === undefined)
    ) {
      this.resetObservation(state);
      return;
    }
    if (state.resumeAt !== undefined) {
      if (
        Date.now() >= state.resumeAt &&
        Date.now() >= this.connectionGraceUntil
      ) {
        state.resumeAt = undefined;
        this.setSubscribed(publication, state, true);
        this.resetObservation(state);
      }
      return;
    }
    if (state.element && state.element !== element)
      this.resetObservation(state);
    if (state.track !== publication.track) this.resetObservation(state);
    state.element = element;
    state.track = publication.track;
    const progress =
      publication.isSubscribed && publication.track
        ? videoProgress(element)
        : undefined;
    if (
      progress !== undefined &&
      state.progress !== undefined &&
      progress > state.progress
    ) {
      state.lastProgress = Date.now();
      state.healthySince ??= Date.now();
      if (state.waitingForRecovery)
        this.log('frames-resumed', publication, state);
      state.waitingForRecovery = false;
      if (Date.now() - state.healthySince >= HEALTHY_RESET_MS) {
        state.attempts = 0;
        state.exhausted = false;
      }
    } else {
      state.healthySince = undefined;
    }
    state.progress = progress;
    if (
      Date.now() - state.lastProgress >= STALL_MS &&
      Date.now() >= this.connectionGraceUntil &&
      Date.now() >= state.nextAttempt
    ) {
      this.recover(publication, state);
    }
  }

  private check = () => {
    this.states.forEach((state, publication) => {
      try {
        this.checkPublication(publication, state);
      } catch (error) {
        this.log('check-error', publication, state, { error: String(error) });
      }
    });
  };
}
